import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import PulseCircleCarousel from "@/components/pulse/PulseCircleCarousel";
import { buildCardSources, splitBodyParagraphs } from "@/lib/cardSources";
import {
  faviconUrl,
  formatSourceDisplayName,
  getDomainLabel,
  toSafeHttpUrl,
  toSafeVideoEmbedUrl,
} from "@/lib/media";
import {
  highlightForParagraph,
  paintParagraphWithHighlight,
} from "@/lib/paintSummaryHighlights";
import type { PulseCard } from "@/types";

interface Props {
  card: PulseCard | null;
  sourceCardBg?: string;
  onClose: () => void;
}

type OverlayPhase = "closed" | "opening" | "shown" | "closing";

const CLOSE_ANIMATION_MS = 220;

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      aria-hidden
    >
      <circle cx="28" cy="28" r="28" fill="#FFFFFF" />
      <path d="M36 20L20 36M20 20L36 36" stroke="#003F28" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SourceArrowIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="news-detail-source-arrow__icon"
      aria-hidden
    >
      <path d="M7 17L16.2 7.8M17 17V7H7" />
    </svg>
  );
}

export default function CardDetailOverlay({ card, sourceCardBg = "#fff5ec", onClose }: Props) {
  const titleId = useId();
  const overlayId = "news-detail-overlay";
  const closeRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const phaseRef = useRef<OverlayPhase>("closed");
  const closeTimerRef = useRef<number | null>(null);
  const [activeCard, setActiveCard] = useState<PulseCard | null>(null);
  const [phase, setPhase] = useState<OverlayPhase>("closed");

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const completeClose = useCallback(
    (notifyParent: boolean) => {
      setActiveCard(null);
      setPhase("closed");
      document.body.style.overflowY = "";
      if (notifyParent) onClose();
    },
    [onClose],
  );

  const close = useCallback(
    (notifyParent = true) => {
      if (phaseRef.current === "closing" || phaseRef.current === "closed") return;
      setPhase("closing");
      clearCloseTimer();
      closeTimerRef.current = window.setTimeout(
        () => completeClose(notifyParent),
        CLOSE_ANIMATION_MS,
      );
    },
    [clearCloseTimer, completeClose],
  );

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (!card) {
      close(false);
      return;
    }

    clearCloseTimer();
    setActiveCard(card);
    setPhase("opening");
    document.body.style.overflowY = "hidden";
  }, [card, clearCloseTimer, close]);

  useEffect(() => {
    if (phase !== "opening") return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    // Force reflow before promoting opening -> shown.
    void overlay.offsetWidth;

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setPhase("shown"));
    });
    closeRef.current?.focus();

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [phase]);

  useEffect(() => {
    if (!activeCard || phase !== "shown") return;

    const root = scrollRef.current;
    if (!root) return;
    root.scrollTop = 0;

    const paragraphs = root.querySelectorAll<HTMLElement>("[data-reveal]");
    const reveal = (el: HTMLElement) => {
      el.classList.remove("opacity-0", "translate-y-[14px]");
      el.classList.add("opacity-100", "translate-y-0");
    };

    if (typeof IntersectionObserver === "undefined") {
      paragraphs.forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          reveal(entry.target as HTMLElement);
          observer.unobserve(entry.target);
        }
      },
      { root, threshold: 0.18 },
    );

    paragraphs.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
    };
  }, [activeCard, phase]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
      document.body.style.overflowY = "";
    };
  }, [clearCloseTimer]);

  if (!activeCard) return null;

  const imageUrl = toSafeHttpUrl(activeCard.image_url);
  const tags = (activeCard.detail.tags ?? []).slice(0, 3);
  const paragraphs = splitBodyParagraphs(activeCard);
  const summaryHighlights = activeCard.summary_highlights ?? [];
  const sources = buildCardSources(activeCard);
  const videoStatus = activeCard.video_status ?? "none";
  const canonicalArticleUrl = toSafeHttpUrl(activeCard.url);
  const verifiedVideos =
    videoStatus === "verified"
      ? (activeCard.videos ?? [])
          .map((video) => {
            const watchUrl = toSafeHttpUrl(video.watch_url);
            // JW Player and many Vimeo clips are domain-locked: the embed works on the
            // publisher site but shows Vimeo's privacy wall on Pulse. Link out instead.
            const externalOnly = video.provider === "jwplayer" || video.provider === "vimeo";
            const embedSrc = externalOnly ? null : toSafeVideoEmbedUrl(video.embed_url);
            const href = externalOnly
              ? ((video.provider === "vimeo" ? canonicalArticleUrl : null) ??
                watchUrl ??
                toSafeVideoEmbedUrl(video.embed_url))
              : watchUrl;
            if (!embedSrc && !href) return null;
            return {
              key: `${video.provider}-${video.video_id}`,
              provider: video.provider,
              embedSrc,
              href,
              title: video.title || "Vídeo de la noticia",
            };
          })
          .filter(
            (
              item,
            ): item is {
              key: string;
              provider: string;
              embedSrc: string | null;
              href: string | null;
              title: string;
            } => item != null,
          )
      : [];
  const showUnverifiedVideoLink = videoStatus === "unverified" && Boolean(canonicalArticleUrl);

  return createPortal(
    <div
      id={overlayId}
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-label="Detalle de noticia"
      aria-hidden={phase === "closed"}
      className={`news-detail-overlay fixed inset-0 z-[10000] ${
        phase === "closed" ? "hidden" : "flex"
      } ${phase === "shown" ? "news-detail-overlay--shown" : ""}`}
    >
      <div id="news-detail-scroll" ref={scrollRef} className="news-detail-panel news-detail-scroll">
        <div className="news-detail-header-logo" aria-hidden="true">
          <PulseCircleCarousel />
        </div>

        <article className="news-detail-content">
          <header className="news-detail-header">
            <button
              ref={closeRef}
              id="news-detail-close"
              type="button"
              aria-label="Cerrar detalle"
              className="news-detail-close flex h-[44px] w-[44px] items-center justify-center p-0 lg:h-[56px] lg:w-[56px]"
              onClick={() => close(true)}
            >
              <CloseIcon />
            </button>
          </header>

          <h2
            id={titleId}
            className="m-0 mx-auto max-w-[670px] px-5 text-center text-[clamp(32px,8vw,46px)] font-bold leading-[0.9] text-[#003F28] lg:px-0 lg:text-[64px]"
          >
            {activeCard.title}
          </h2>

          <div
            id="news-detail-tags"
            className={`mt-6 flex flex-wrap justify-center gap-2 px-5 lg:mt-[41px] lg:px-0 ${
              tags.length === 0 ? "hidden" : ""
            }`}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-[4px] bg-[#ebd9ca] px-[10px] py-[10px] text-[16px] font-semibold leading-none text-[#003f2a]"
              >
                {tag}
              </span>
            ))}
          </div>

          <div
            id="news-detail-image-wrap"
            className={imageUrl ? "" : "hidden"}
            style={{ "--news-detail-source-bg": sourceCardBg } as CSSProperties}
          >
            {imageUrl && (
              <>
                <div className="news-detail-image-backing" aria-hidden />
                <img
                  id="news-detail-image"
                  src={imageUrl}
                  alt=""
                  width={1090}
                  height={595}
                  decoding="async"
                  className="pointer-events-none"
                />
              </>
            )}
          </div>

          <div
            id="news-detail-body"
            className="mx-auto mt-12 max-w-[670px] space-y-6 px-5 lg:mt-20 lg:space-y-7 lg:px-0"
          >
            {paragraphs.map((paragraph, index) => (
              <p
                key={index}
                data-reveal
                className="news-detail-paragraph m-0 translate-y-[14px] opacity-0 transition-all duration-700 ease-out"
              >
                {paintParagraphWithHighlight(
                  paragraph,
                  highlightForParagraph(summaryHighlights, index),
                )}
              </p>
            ))}

            {verifiedVideos.length > 0 && (
              <div id="news-detail-videos" className="space-y-4" data-reveal>
                {verifiedVideos.map((video) =>
                  video.embedSrc ? (
                    <div
                      key={video.key}
                      className="relative aspect-video w-full overflow-hidden rounded-[12px] bg-[#003f28]"
                    >
                      <iframe
                        src={video.embedSrc}
                        title={video.title}
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 h-full w-full border-0"
                      />
                    </div>
                  ) : video.href ? (
                    <a
                      key={video.key}
                      href={video.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative flex aspect-video w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-[12px] bg-[#003f28] px-6 text-center no-underline transition-opacity hover:opacity-95"
                    >
                      <span className="text-[22px] font-bold leading-tight text-white lg:text-[28px]">
                        Ver vídeo
                      </span>
                      <span className="max-w-[28rem] text-[15px] font-medium leading-snug text-white/85 lg:text-[16px]">
                        {video.provider === "vimeo"
                          ? "Este vídeo solo se puede ver en el medio original (restricción de Vimeo)"
                          : "Haz clic aquí para reproducirlo en una pestaña nueva"}
                      </span>
                    </a>
                  ) : null,
                )}
              </div>
            )}

            {showUnverifiedVideoLink && canonicalArticleUrl && (
              <p data-reveal className="m-0">
                <a
                  href={canonicalArticleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[16px] font-semibold text-[#003F28] underline underline-offset-2"
                >
                  [Vídeo en el link]
                </a>
              </p>
            )}
          </div>

          <div
            id="news-detail-sources-list"
            className={`news-detail-sources-list px-5 lg:px-0 ${
              sources.length === 0 ? "hidden" : ""
            }`}
          >
            {sources.map((source) => (
              <a
                key={source.url}
                className="news-detail-source-item"
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="news-detail-source-content">
                  <p className="news-detail-source-title">{source.title}</p>
                  <div className="news-detail-source-meta">
                    <img
                      className="news-detail-source-meta__icon"
                      src={faviconUrl(source.url)}
                      alt=""
                      width={12}
                      height={12}
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <span className="news-detail-source-meta__domain">
                      {formatSourceDisplayName(source.sourceLabel, source.url) || getDomainLabel(source.url)}
                    </span>
                  </div>
                </div>
                <span className="news-detail-source-arrow" aria-hidden>
                  <SourceArrowIcon />
                </span>
              </a>
            ))}
          </div>
        </article>
      </div>
    </div>,
    document.body,
  );
}
